import { motion } from "framer-motion";
import { ArrowRight, Check, Bot, MessageSquare, Calendar, Users, Zap, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { AnimatedCounter } from "@/components/ui/AnimatedCounter";
import { Link } from "react-router-dom";

const useCases = [
  {
    icon: MessageSquare,
    title: "Lead Qualification Bot",
    description: "Automatically qualify leads 24/7, ask the right questions, and route hot prospects to your sales team.",
  },
  {
    icon: Users,
    title: "Customer Support Agent",
    description: "Handle common support queries instantly, reducing response times and freeing your team for complex issues.",
  },
  {
    icon: Calendar,
    title: "Appointment Booking Assistant",
    description: "Let prospects book meetings directly through chat, synced with your calendar and CRM.",
  },
  {
    icon: Zap,
    title: "Sales Outreach Automation",
    description: "Personalized follow-ups and nurture sequences powered by AI that learns from your best performers.",
  },
  {
    icon: Bot,
    title: "Internal Knowledge Bot",
    description: "Give your team instant access to company knowledge, SOPs, and documentation through natural conversation.",
  },
  {
    icon: Shield,
    title: "Onboarding Assistant",
    description: "Guide new customers or employees through onboarding with personalized, interactive experiences.",
  },
];

const packages = [
  {
    name: "Starter Agent",
    price: "$5,000",
    timeline: "2-3 weeks",
    features: [
      "Single-purpose AI bot",
      "1 platform integration",
      "Basic conversation flows",
      "30 days of refinement",
      "Training documentation",
    ],
  },
  {
    name: "Pro Agent",
    price: "$10,000",
    timeline: "4-5 weeks",
    popular: true,
    features: [
      "Multi-purpose AI bot",
      "3 platform integrations",
      "Advanced conversation logic",
      "CRM/tool integrations",
      "60 days of refinement",
      "Custom training on your data",
      "Analytics dashboard",
    ],
  },
  {
    name: "Enterprise Agent",
    price: "$15,000",
    timeline: "6-8 weeks",
    features: [
      "Full AI automation suite",
      "Unlimited integrations",
      "Custom AI model training",
      "Multi-language support",
      "90 days of refinement",
      "Dedicated success manager",
      "Priority support",
      "Ongoing optimization",
    ],
  },
];

const processSteps = [
  {
    step: "01",
    title: "Discovery Call",
    description: "We learn about your workflows, pain points, and goals to design the perfect AI agent.",
  },
  {
    step: "02",
    title: "Design & Strategy",
    description: "Our team creates conversation flows, integration plans, and the technical architecture.",
  },
  {
    step: "03",
    title: "Build & Train",
    description: "We develop your custom AI agent and train it on your specific use case and data.",
  },
  {
    step: "04",
    title: "Test & Refine",
    description: "Rigorous testing with your team to ensure the agent performs flawlessly.",
  },
  {
    step: "05",
    title: "Launch & Monitor",
    description: "We deploy your agent and monitor performance, making adjustments as needed.",
  },
];

const portfolio = [
  {
    company: "TechScale SaaS",
    result: "80% of support queries automated",
    type: "Customer Support Agent",
    image: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=600&q=80",
  },
  {
    company: "DevForce Agency",
    result: "3x increase in qualified leads",
    type: "Lead Qualification Bot",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=600&q=80",
  },
  {
    company: "AI Solutions Co",
    result: "50% reduction in no-shows",
    type: "Appointment Booking Assistant",
    image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=600&q=80",
  },
];

export default function AIAgents() {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/50 to-background pointer-events-none" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background text-sm font-medium mb-8">
              One-Time Project
            </span>
            <h1 className="heading-hero mb-6">
              Custom AI Agents
              <br />
              <span className="text-muted-foreground">Built for Your Business</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
              Get a fully-built AI agent tailored to your specific needs. 
              No monthly retainer required. Starting at $5,000.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg" 
                className="rounded-full px-8"
                data-cal-link="thecloudseo/30-min-meeting"
                data-cal-namespace="30-min-meeting"
                data-cal-config='{"layout":"month_view"}'
              >
                Book a Consultation <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button variant="outline" size="lg" className="rounded-full px-8">
                View Portfolio
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-b border-border">
        <div className="container-wide">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: 24, suffix: "/7", label: "Availability" },
              { value: 80, suffix: "%", label: "Queries Automated" },
              { value: 5, suffix: "x", label: "Response Speed" },
              { value: 50, suffix: "+", label: "Agents Built" },
            ].map((stat, index) => (
              <AnimatedSection key={stat.label} delay={index * 0.1}>
                <div className="text-4xl md:text-5xl font-bold mb-2">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-muted-foreground text-sm">{stat.label}</div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-spacing">
        <div className="container-wide">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="heading-section mb-6">What Can AI Agents Do?</h2>
            <p className="text-xl text-muted-foreground">
              From lead qualification to customer support, AI agents handle the 
              repetitive work so your team can focus on high-value activities.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((useCase, index) => (
              <AnimatedSection key={useCase.title} delay={index * 0.1}>
                <div className="premium-card p-8 h-full">
                  <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center mb-6">
                    <useCase.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{useCase.title}</h3>
                  <p className="text-muted-foreground">{useCase.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="section-spacing bg-secondary/30">
        <div className="container-wide">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="heading-section mb-6">Choose Your Package</h2>
            <p className="text-xl text-muted-foreground">
              One-time investment. No monthly fees. Your AI agent, built and delivered.
            </p>
          </AnimatedSection>

          <div className="grid lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <AnimatedSection key={pkg.name} delay={index * 0.1}>
                <div className={`relative rounded-3xl border p-8 transition-all h-full ${
                  pkg.popular
                    ? "bg-primary text-primary-foreground border-primary scale-105"
                    : "bg-card border-border"
                }`}>
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-foreground text-background text-xs font-semibold px-4 py-1.5 rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <h3 className="text-xl font-semibold mb-2">{pkg.name}</h3>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl font-bold">{pkg.price}</span>
                  </div>
                  <p className={`text-sm mb-6 ${pkg.popular ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                    Timeline: {pkg.timeline}
                  </p>

                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          pkg.popular ? "bg-primary-foreground/20" : "bg-secondary"
                        }`}>
                          <Check className="w-3 h-3" />
                        </div>
                        <span className={`text-sm ${pkg.popular ? "text-primary-foreground/90" : "text-muted-foreground"}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full rounded-full ${
                      pkg.popular
                        ? "bg-primary-foreground text-primary hover:bg-primary-foreground/90"
                        : ""
                    }`}
                    variant={pkg.popular ? "default" : "outline"}
                    data-cal-link="thecloudseo/30-min-meeting"
                    data-cal-namespace="30-min-meeting"
                    data-cal-config='{"layout":"month_view"}'
                  >
                    Get Started
                  </Button>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-spacing">
        <div className="container-wide">
          <AnimatedSection className="max-w-3xl mb-16">
            <h2 className="heading-section mb-6">How It Works</h2>
            <p className="text-xl text-muted-foreground">
              From discovery to deployment in 2-8 weeks, depending on complexity.
            </p>
          </AnimatedSection>

          <div className="space-y-8">
            {processSteps.map((step, index) => (
              <AnimatedSection key={step.step} delay={index * 0.1}>
                <div className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-muted-foreground/30 w-16 flex-shrink-0">
                    {step.step}
                  </div>
                  <div className="flex-1 pb-8 border-b border-border last:border-0">
                    <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio */}
      <section className="section-spacing bg-primary text-primary-foreground">
        <div className="container-wide">
          <AnimatedSection className="text-center mb-16">
            <h2 className="heading-section mb-6">Success Stories</h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {portfolio.map((item, index) => (
              <AnimatedSection key={item.company} delay={index * 0.1}>
                <div className="rounded-2xl overflow-hidden bg-primary-foreground/10 border border-primary-foreground/20">
                  <div className="aspect-video relative">
                    <img 
                      src={item.image} 
                      alt={item.company}
                      className="w-full h-full object-cover opacity-80"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary to-transparent" />
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-primary-foreground/70 mb-2">{item.type}</p>
                    <h3 className="text-xl font-semibold mb-2">{item.company}</h3>
                    <p className="text-2xl font-bold">{item.result}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing">
        <div className="container-narrow text-center">
          <AnimatedSection>
            <h2 className="heading-section mb-6">
              Ready to Build Your AI Agent?
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
              Book a free consultation to discuss your needs and get a custom quote.
            </p>
            <Button 
              size="lg" 
              className="rounded-full px-10 text-base h-14"
              data-cal-link="thecloudseo/30-min-meeting"
              data-cal-namespace="30-min-meeting"
              data-cal-config='{"layout":"month_view"}'
            >
              Book a Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
